import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Search, 
  Home,
  Compass,
  Plus,
  Heart,
  Bookmark,
  ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useClickOutside } from '../hooks';
import { usePost } from '../contexts/PostContext';
import ProfileDropdown from './ProfileDropdown';

interface SearchResult {
  id: number;
  type: 'user' | 'post';
  username?: string;
  fullName?: string;
  title?: string;
  author?: string;
}

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { user, userProfile, signOut } = useAuth();
  const { openCreatePost, closeCreatePost } = usePost();
  const navigate = useNavigate();
  const location = useLocation();

  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const profileDropdownRef = useRef(null);

  useClickOutside(searchRef, () => setShowSearchResults(false));
  useClickOutside(menuRef, () => setIsOpen(false));
  useClickOutside(profileDropdownRef, () => setShowProfileDropdown(false));

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    setIsSearching(true);
    setShowSearchResults(true);

    if (query.trim().length >= 2) {
      try {
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const mockResults: SearchResult[] = [
          { 
            id: 1, 
            type: 'user' as const, 
            username: 'usuario1', 
            fullName: 'Usuário Um' 
          },
          { 
            id: 2, 
            type: 'post' as const, 
            title: 'Post de Exemplo', 
            author: 'usuario1' 
          },
        ].filter(item => 
          item.type === 'user' 
            ? (item.username?.includes(query) || item.fullName?.toLowerCase().includes(query.toLowerCase()))
            : item.title?.toLowerCase().includes(query.toLowerCase())
        );

        setSearchResults(mockResults);
      } catch (error) {
        console.error('Erro na pesquisa:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const getMenuItems = () => {
    const items = [];

    if (user && userProfile) {
      items.push(
        { href: `/perfil/${userProfile.username}`, label: 'Meu Perfil', icon: User },
        { href: '/feed', label: 'Feed', icon: Home },
        { href: '/explorar', label: 'Explorar', icon: Compass },
        { onClick: handleSignOut, label: 'Sair', icon: LogOut }
      );
    }

    return items;
  };

  const menuItems = getMenuItems();

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  // Componente de Resultados da Pesquisa
  const SearchResults = () => {
    if (!showSearchResults) return null;

    return (
      <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50">
        {isSearching ? (
          <div className="p-4 text-center text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-sm">Pesquisando...</p>
          </div>
        ) : searchResults.length > 0 ? (
          <div className="py-2">
            {searchResults.map((result: SearchResult) => (
              <Link
                key={`${result.type}-${result.id}`}
                to={`/perfil/${result.username}`}
                className="block px-4 py-2 hover:bg-gray-50 transition-colors"
                onClick={() => setShowSearchResults(false)}
              >
                <div className="flex items-center">
                  {result.type === 'user' ? (
                    <>
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex-shrink-0"></div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{result.fullName}</p>
                        <p className="text-sm text-gray-500">@{result.username}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-8 h-8 rounded bg-gray-200 flex-shrink-0"></div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{result.title}</p>
                        <p className="text-sm text-gray-500">por @{result.author}</p>
                      </div>
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : searchQuery.length >= 2 ? (
          <div className="p-4 text-center text-gray-500">
            <p className="text-sm">Nenhum resultado encontrado</p>
          </div>
        ) : searchQuery.length > 0 ? (
          <div className="p-4 text-center text-gray-500">
            <p className="text-sm">Digite pelo menos 2 caracteres</p>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <nav className="bg-white shadow-sm fixed w-full top-0 z-50">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <span className="text-xl font-bold text-indigo-600">InspiraFolio</span>
              <span className="ml-2 px-2 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-600 rounded-full">
                beta v1.0
              </span>
            </Link>
          </div>

          {/* Menu do Usuário e Navegação */}
          <div className="flex items-center space-x-1">
            {user ? (
              <>
                {/* Links de Navegação Desktop */}
                <div className="flex items-center space-x-1">
                  <Link
                    to="/feed"
                    className={`p-2 rounded-lg inline-flex items-center transition-colors ${
                      isActivePath('/feed')
                        ? 'text-indigo-600 bg-indigo-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Home className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/explorar"
                    className={`p-2 rounded-lg inline-flex items-center transition-colors ${
                      isActivePath('/explorar')
                        ? 'text-indigo-600 bg-indigo-50'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Compass className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => openCreatePost()}
                    className="p-2 rounded-lg inline-flex items-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Nova Publicação"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                  
                  {/* Profile Avatar and Dropdown */}
                  <div className="relative" ref={profileDropdownRef}>
                    <button
                      onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                      className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <img
                        src={userProfile?.avatar_url || '/default-avatar.png'}
                        alt={userProfile?.username}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    </button>

                    {showProfileDropdown && (
                      <ProfileDropdown
                        userProfile={userProfile}
                        onClose={() => setShowProfileDropdown(false)}
                        onSignOut={handleSignOut}
                      />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/entrar"
                  className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Entrar
                </Link>
                <Link
                  to="/cadastro"
                  className="bg-indigo-600 text-white hover:bg-indigo-700 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Cadastrar
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;